/** @file Builds the dock: restores a saved layout (retitled to the current language) or the default one. */
import type { Translate } from '@pose2d/i18n/types';
import type { AddPanelOptions, AddPanelPositionOptions, SerializedDockview } from 'dockview-react';
import type { PanelEntry } from '../panels/panelEntry';
import { scheduleLayoutSave } from '../shell/settings/scheduleLayoutSave';

/** The DockviewApi methods applyLayout uses (a fake in tests). */
export interface DockTarget {
  addPanel(options: AddPanelOptions): unknown;
  fromJSON(data: SerializedDockview): void;
  clear(): void;
  getPanel(id: string): { api: { setTitle(title: string): void; setActive(): void } } | undefined;
}

interface Placement {
  reference?: string;
  direction: 'left' | 'right' | 'below' | 'within';
}

const ORDER = ['viewport', 'hierarchy', 'assets', 'inspector', 'timeline'];
/** Wave-3 panels dock as tabs of the Inspector's group (or the Timeline's, for Animations); `within` needs the reference present (W3-06). */
const PLACEMENT: Record<string, Placement | undefined> = {
  hierarchy: { reference: 'viewport', direction: 'left' },
  assets: { reference: 'hierarchy', direction: 'below' },
  inspector: { reference: 'viewport', direction: 'right' },
  timeline: { direction: 'below' },
  animations: { reference: 'timeline', direction: 'within' },
  weights: { reference: 'inspector', direction: 'within' },
  directions: { reference: 'inspector', direction: 'within' },
  skins: { reference: 'inspector', direction: 'within' },
  drawOrder: { reference: 'inspector', direction: 'within' },
  constraints: { reference: 'inspector', direction: 'within' },
  events: { reference: 'inspector', direction: 'within' },
  sockets: { reference: 'inspector', direction: 'within' },
  autorig: { reference: 'inspector', direction: 'within' },
  activity: { reference: 'inspector', direction: 'within' },
  aiView: { reference: 'inspector', direction: 'within' },
};

const rank = (id: string): number => {
  const i = ORDER.indexOf(id);
  return i < 0 ? ORDER.length : i;
};

function position(id: string, added: ReadonlySet<string>): { position?: AddPanelPositionOptions } {
  const place = PLACEMENT[id];
  if (place === undefined) return {};
  if (place.reference === undefined) return { position: { direction: place.direction } };
  return added.has(place.reference) ? { position: { referencePanel: place.reference, direction: place.direction } } : {};
}

/** The ids of `panels` and of the panels they dock beside that the dock has right now. */
function presentIn(dock: DockTarget, panels: readonly PanelEntry[]): Set<string> {
  const known = new Set([...panels.map((p) => p.id), ...panels.flatMap((p) => PLACEMENT[p.id]?.reference ?? [])]);
  return new Set([...known].filter((id) => dock.getPanel(id) !== undefined));
}

/** Adds every panel not in `present`, in dependency order, beside its default neighbour when that one is in the dock. */
function addMissing(dock: DockTarget, panels: readonly PanelEntry[], t: Translate, present: Set<string>): void {
  for (const p of [...panels].sort((a, b) => rank(a.id) - rank(b.id))) {
    if (present.has(p.id)) continue;
    dock.addPanel({ id: p.id, component: p.id, title: t(p.title), ...(p.renderer === undefined ? {} : { renderer: p.renderer }), ...position(p.id, present) });
    present.add(p.id);
  }
}

function restore(dock: DockTarget, saved: unknown, panels: readonly PanelEntry[], t: Translate): boolean {
  try {
    dock.fromJSON(saved as SerializedDockview);
  } catch {
    dock.clear();
    return false;
  }
  for (const p of panels) dock.getPanel(p.id)?.api.setTitle(t(p.title));
  return true;
}

/** Which layout was applied; a restored layout gains panels it lacks, an unreadable one is replaced by the default and cleared. With no saved layout it adds only the panels the dock lacks (openPanel's way back for a closed tab). */
export function applyLayout(dock: DockTarget, saved: unknown, panels: readonly PanelEntry[], t: Translate): 'saved' | 'default' {
  if (saved !== null && saved !== undefined) {
    if (restore(dock, saved, panels, t)) {
      addMissing(dock, panels, t, presentIn(dock, panels));
      return 'saved';
    }
    scheduleLayoutSave(null);
  }
  addMissing(dock, panels, t, presentIn(dock, panels));
  return 'default';
}
